/**
 * Structural proof that a turn's outgoing message came from a real click on the prescription
 * summary card's Send / Send all button -- never from typed chat or voice input, even when the
 * words are identical to what the button emits. Backend gate: SEND_THROUGH_BUTTON_ONLY
 * (orchestration_agent's agents/safeguards.py).
 *
 * Mirrors the existing "pending, consumed once at payload build, matched against the exact
 * outgoing text" pattern already used for consumePendingSelectedPharmacy /
 * consumePendingSelectedFavouriteDrug in prescriptionUtils.ts. Set ONLY by the Send / Send all
 * onClick handlers in PrescriptionSummary/index.tsx -- never by the chat box or voice input,
 * which is exactly what makes them structurally unable to set this marker.
 */

import { STORAGE_KEYS } from "./constants";

const normalize = (value: string): string => (value || "").trim().replace(/\s+/g, " ").toLowerCase();

/** Called from the Send / Send all button's onClick with the exact message it is about to submit. */
export const markSendOriginButton = (message: string): void => {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.setItem(
      STORAGE_KEYS.PENDING_SEND_ORIGIN,
      JSON.stringify({ selectionText: normalize(message) })
    );
  } catch {
    // Best-effort: a storage failure here just means the marker is not set, which fails
    // closed (send stays gated behind text-based signals / the button-only deny path) --
    // never fails open.
  }
};

/**
 * Consume-once. Returns "button" only when `text` is the exact (whitespace and case-normalized)
 * message the most recent click staged -- so a queued/typed submission that races the click, or a
 * click swallowed by an in-flight-search guard, can never inherit the marker for unrelated
 * text. Always clears on read, matched or not, so a stale marker never survives to a later turn.
 */
export const consumePendingSendOrigin = (text: string): "button" | undefined => {
  if (typeof sessionStorage === "undefined") {
    return undefined;
  }
  const raw = sessionStorage.getItem(STORAGE_KEYS.PENDING_SEND_ORIGIN);
  console.log("[consumePendingSendOrigin] Called with text:", text, "stored marker:", raw);
  if (!raw) {
    console.log("[consumePendingSendOrigin] No marker found");
    return undefined;
  }
  sessionStorage.removeItem(STORAGE_KEYS.PENDING_SEND_ORIGIN);
  try {
    const parsed = JSON.parse(raw) as { selectionText?: string };
    const staged = normalize(parsed.selectionText || "");
    const payloadText = normalize(text);
    const match = staged && payloadText && staged === payloadText;
    console.log("[consumePendingSendOrigin] Staged:", staged, "Payload:", payloadText, "Match:", match);
    return match ? "button" : undefined;
  } catch (err) {
    console.error("[consumePendingSendOrigin] Parse error:", err);
    return undefined;
  }
};

/** Clears any armed marker without consuming it -- used on session/chat reset. */
export const clearPendingSendOrigin = (): void => {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.removeItem(STORAGE_KEYS.PENDING_SEND_ORIGIN);
};
